import { useCallback } from 'react';
import type { ZendeskMessagingHookResult } from './types';

type ZEFunction = (action: string, ...args: unknown[]) => unknown;

function getZE(): ZEFunction | null {
  return (window as Window & { zE?: ZEFunction }).zE ?? null;
}

/**
 * Tracks whether a consumer (e.g. ChatOverlay) explicitly called setHidden(true).
 * ZendeskMessagingProvider reads this before calling zE('messenger', 'show') after loginUser,
 * preventing a race condition where the JWT callback fires after the overlay has already hidden the widget.
 */
let hiddenByConsumer = false;

export function isZendeskHiddenByConsumer(): boolean {
  return hiddenByConsumer;
}

/**
 * Hook to control Zendesk widget visibility.
 * - `setHidden` — hide/show the floating Zendesk launcher (calls zE directly)
 *
 * @example
 * const { setHidden } = useZendeskMessaging();
 *
 * // Hide widget while chat overlay is open
 * useEffect(() => {
 *   setHidden(true);
 *   return () => setHidden(false);
 * }, [setHidden]);
 */
export const useZendeskMessaging = (): ZendeskMessagingHookResult => {
  const setHidden = useCallback((hidden: boolean) => {
    hiddenByConsumer = hidden;
    const zE = getZE();
    if (!zE) return;
    try {
      zE('messenger', hidden ? 'hide' : 'show');
    } catch { /* SDK not yet fully initialized */ }
  }, []);

  return { setHidden };
};
